import { useEffect, useState } from 'react';
import { Tab } from '@headlessui/react';
import Alert from '@v2/shared/components/Alert';
import Breadcrumbs from '@v2/shared/components/Breadcrumbs';
import Spinner from '@v2/shared/components/Spinner';
import { cn } from '@v2/shared/lib/utils';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import List from './List';
import useAutoRenewals from './useAutoRenewals';
import { Props } from './types';

/** The contact form's "General Inquiry or Comment" — see ContactForm/subjects.js. */
const FEEDBACK_SUBJECT = 6;

/** The account dashboard, still legacy customer.php — the same entry the menu opens with. */
const ACCOUNT_URL = '/customer?xCmd=account';

const TAB_CLASSES = cn(
  'block md:inline-block p-1 pt-0 pb-1.5 group font-semibold cursor-pointer',
  'bg-transparent border-transparent border-t-[1.5px] border-x-[1.5px] border-b-0 -mb-[1.5px]',
  'text-slate-600 hover:text-slate-900',
  'ui-selected:bg-white ui-selected:text-slate-900 ui-selected:border-slate-200 rounded-t'
);

/**
 * /my-account/discountlock — the customer's auto-renewing subscriptions.
 *
 * Off headless_jshop, and kept the shape it had there: the server renders the heading,
 * the sentence under it and the frame, and the subscriptions arrive over the storefront
 * api once this has mounted, under a spinner. Nothing behind the login wall is indexed,
 * so a complete first paint would buy nothing — and props that are the same for every
 * customer are props that can be cached, which buys quite a lot.
 *
 * Not one line of it names the service. Every brand calls this something else —
 * DiscountLock here, PriceShield on MagSpot, Renew & Save on TopMags — and the name
 * arrives as a prop off sites config, which is what will let a second brand mount this
 * same component. See AutoRenewalsPagePropsBuilder.
 */
export default function AutoRenewals({
  autoRenewServiceName,
  aboutUrl,
  loginUrl,
  manageUrlTemplate,
}: Props) {
  const { buildUrl } = useUrlGenerator();
  const { active, inactive, needsAttentionCount, isLoading, hasFailed } = useAutoRenewals(loginUrl);

  const showInactive = inactive.length > 0;
  const [selectedTab, setSelectedTab] = useState<'active' | 'inactive'>('active');

  // Read once, on mount: the tabs are not in the server's markup, so there is nothing to
  // disagree with, and the account dashboard links straight to #inactive.
  useEffect(() => {
    if (window.location.hash === '#inactive') {
      setSelectedTab('inactive');
    }
  }, []);

  return (
    <div>
      {/* This page's trail, not the layout's — see SideMenu. */}
      <Breadcrumbs items={[{ label: 'Account', href: ACCOUNT_URL }, { label: autoRenewServiceName }]} />

      <h1 className="font-bold text-2xl md:text-3xl mb-3">{autoRenewServiceName}</h1>

      <p className="text-sm md:text-base text-slate-600 mb-5 md:mb-10 font-medium max-w-3xl">
        Manage your {autoRenewServiceName} subscriptions. You can learn more about {autoRenewServiceName}{' '}
        {/* Themed, not `red-600`: this is the brand's accent, and two of the four brands
            reaching this page are not red. See styles/v2.css. */}
        <a href={buildUrl(aboutUrl)} className="text-primary hover:text-primary-700 active:text-primary-800">
          here
        </a>
        .
      </p>

      {isLoading && (
        <div className="w-full flex items-center justify-center py-10">
          <Spinner className="text-primary text-2xl" />
        </div>
      )}

      {!isLoading && hasFailed && (
        <Alert color="danger">Oops, something went wrong. Try again later.</Alert>
      )}

      {!isLoading && !hasFailed && (
        <>
          <Alert color="info">
            <div className="font-semibold mb-1">New! Improved {autoRenewServiceName} Management</div>
            We&apos;ve made some improvements to this page in order to make it easier for you to manage your{' '}
            {autoRenewServiceName} subscriptions.
            <br />
            If you have any questions or feedback on this new experience,{' '}
            <a
              href={buildUrl(`/contact?xSubject=${FEEDBACK_SUBJECT}`)}
              target="_blank"
              rel="noreferrer"
              className="underline font-medium"
            >
              let us know here
            </a>
            .
          </Alert>

          <Tab.Group
            selectedIndex={selectedTab === 'inactive' && showInactive ? 1 : 0}
            onChange={(index) => {
              const tab = index === 1 ? 'inactive' : 'active';

              setSelectedTab(tab);
              // replaceState rather than a navigation: the hash is here so the tab can be
              // linked to and survive a reload, not so that Back steps through tabs.
              window.history.replaceState(null, '', `#${tab}`);
            }}
          >
            <Tab.List className="flex">
              <StyledTab label="Active" count={active.length} />
              {showInactive && <StyledTab label="Inactive" count={inactive.length} />}
            </Tab.List>

            <Tab.Panels className="border-[1.5px] border-solid border-slate-200 rounded mb-10">
              <Tab.Panel>
                {needsAttentionCount > 0 && (
                  <Alert color="warning" className="m-2 mb-0">
                    <span className="font-semibold">Action Required</span> —{' '}
                    {needsAttentionCount > 1 ? 'Some' : 'One'} of your subscriptions need
                    {needsAttentionCount > 1 ? '' : 's'} your attention.
                  </Alert>
                )}

                <List
                  autoRenewals={active}
                  autoRenewServiceName={autoRenewServiceName}
                  manageUrlTemplate={manageUrlTemplate}
                />
              </Tab.Panel>

              {showInactive && (
                <Tab.Panel>
                  <List
                    autoRenewals={inactive}
                    autoRenewServiceName={autoRenewServiceName}
                    manageUrlTemplate={manageUrlTemplate}
                  />
                </Tab.Panel>
              )}
            </Tab.Panels>
          </Tab.Group>
        </>
      )}
    </div>
  );
}

function StyledTab({ label, count }: { label: string; count: number }) {
  return (
    <Tab className={TAB_CLASSES}>
      {({ selected }) => (
        <div className={cn('px-5 py-2.5 rounded', !selected && 'group-hover:bg-slate-100')}>
          <span className="mr-1.5">{label}</span>
          <span
            className={cn(
              'rounded-full px-2 bg-slate-100 text-slate-500 text-sm',
              !selected && 'group-hover:bg-slate-200 group-hover:text-slate-600'
            )}
          >
            {count}
          </span>
        </div>
      )}
    </Tab>
  );
}

AutoRenewals.layoutName = 'Layouts.Account';
